// src/PageInviteMultiple.js
import { useState } from 'react';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import heic2any from 'heic2any';

const DIAPORAMA_ID = 'wGkKHFfvUaEbDjM3Sxw0';
const NOM_COLLECTION = '50iemeFrancineYvon'; // ⚠️ vérifie l'orthographe exacte

const TAILLE_MAX_PHOTO = 5 * 1024 * 1024;   // 5 Mo
const TAILLE_MAX_VIDEO = 100 * 1024 * 1024; // 100 Mo

function PageInviteMultiple() {
  const [codeEntre, setCodeEntre] = useState('');
  const [accesAutorise, setAccesAutorise] = useState(false);
  const [televersementActif, setTeleversementActif] = useState(true);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);

  const [nomAuteur, setNomAuteur] = useState('');
  const [message, setMessage] = useState('');
  const [fichiers, setFichiers] = useState([]); // liste de { fichier, statut, erreur }
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const verifierCode = async () => {
    setErreur('');
    setChargement(true);
    try {
      const refDocument = doc(db, NOM_COLLECTION, DIAPORAMA_ID);
      const snapshot = await getDoc(refDocument);

      if (!snapshot.exists()) {
        setErreur('Diaporama introuvable.');
        setChargement(false);
        return;
      }

      const donnees = snapshot.data();      
      if (donnees.codeAcces === codeEntre.trim()) {
        setAccesAutorise(true);
        setTeleversementActif(donnees.televersementActif);
      } else {
        setErreur("Code d'accès incorrect.");
      }
    } catch (err) {
      console.error(err);
      setErreur('Une erreur est survenue. Réessaie.');
    }
    setChargement(false);
  };

  // Quand l'usager choisit ses fichiers, on prépare la liste avec un statut initial
  const gererSelectionFichiers = (e) => {
    const nouveauxFichiers = Array.from(e.target.files).map((f) => ({
      fichier: f,
      statut: 'en_attente', // en_attente | envoi | succes | erreur
      erreur: null
    }));
    setFichiers(nouveauxFichiers);
  };

  const retirerFichier = (index) => {
    setFichiers((liste) => liste.filter((_, i) => i !== index));
  };

  const preparerFichier = async (fichier) => {
  const estHeic =
    fichier.type === 'image/heic' ||
    fichier.type === 'image/heif' ||
    fichier.name.toLowerCase().endsWith('.heic') ||
    fichier.name.toLowerCase().endsWith('.heif');

  if (!estHeic) {
    return fichier; // rien à faire, fichier normal
  }

  // Conversion HEIC → JPEG
  const blobConverti = await heic2any({
    blob: fichier,
    toType: 'image/jpeg',
    quality: 0.8
  });

  const blobFinal = Array.isArray(blobConverti) ? blobConverti[0] : blobConverti;
  const nouveauNom = fichier.name.replace(/\.(heic|heif)$/i, '.jpg');

  return new File([blobFinal], nouveauNom, { type: 'image/jpeg' });
};

const envoyerUnFichier = async (item) => {
  const fichierPret = await preparerFichier(item.fichier);

  const type = fichierPret.type.startsWith('video') ? 'video' : 'photo';
  const limite = type === 'video' ? TAILLE_MAX_VIDEO : TAILLE_MAX_PHOTO;

  if (fichierPret.size > limite) {
    const limiteMo = type === 'video' ? '100 Mo' : '5 Mo';
    throw new Error(`Trop volumineux (limite ${limiteMo})`);
  }

  const nomFichierUnique = `${Date.now()}_${fichierPret.name}`;
  const refFichier = ref(storage, `medias/${DIAPORAMA_ID}/${nomFichierUnique}`);
  await uploadBytes(refFichier, fichierPret, { contentType: fichierPret.type });
  const url = await getDownloadURL(refFichier);

  await addDoc(collection(db, 'medias'), {
    diaporamaId: DIAPORAMA_ID,
    type,
    url,
    dateAjout: serverTimestamp(),
    nomAuteur: nomAuteur.trim(),
    message: message.trim().slice(0, 100)
  });
};

  const envoyerTousLesFichiers = async () => {
    if (fichiers.length === 0) {
      setErreur('Choisis au moins une photo ou vidéo.');
      return;
    }
    if (!nomAuteur.trim()) {
      setErreur('Entre ton nom.');
      return;
    }

    setErreur('');
    setEnvoiEnCours(true);

    // On envoie les fichiers un par un et on met à jour leur statut au fur et à mesure
    for (let i = 0; i < fichiers.length; i++) {
      setFichiers((liste) => {
        const copie = [...liste];
        copie[i] = { ...copie[i], statut: 'envoi' };
        return copie;
      });

      try {
        await envoyerUnFichier(fichiers[i]);
        setFichiers((liste) => {
          const copie = [...liste];
          copie[i] = { ...copie[i], statut: 'succes' };
          return copie;
        });
      } catch (err) {
        console.error(err);
        setFichiers((liste) => {
          const copie = [...liste];
          copie[i] = { ...copie[i], statut: 'erreur', erreur: err.message };
          return copie;
        });
      }
    }

    setEnvoiEnCours(false);
  };

  if (!accesAutorise) {
    return (
      <div>
        <h2>Entrez le code d'accès</h2>
        <input
          type="text"
          value={codeEntre}
          onChange={(e) => setCodeEntre(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              verifierCode();
            }
          }}
          placeholder="Code d'accès"
        />
        <button onClick={verifierCode} disabled={chargement}>
          {chargement ? 'Vérification...' : 'Valider'}
        </button>
        {erreur && <p style={{ color: 'red' }}>{erreur}</p>}
      </div>
    );
  }

  if (!televersementActif) {
    return <p>Le téléversement est actuellement désactivé par l'organisateur.</p>;
  }

  return (
    <div>
      <h2>Ajouter plusieurs photos ou vidéos</h2>

      <div>
        <label>Ton nom : </label>
        <input
          type="text"
          value={nomAuteur}
          onChange={(e) => setNomAuteur(e.target.value)}
          maxLength={50}
          placeholder="Ton nom"
        />
      </div>

      <div>
        <label>Message (optionnel, appliqué à tous les fichiers) : </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={100}
          placeholder="Un petit mot..."
        />
        <p>{message.length}/100</p>
      </div>

      <div>
        <input
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={gererSelectionFichiers}
        />
      </div>

      {fichiers.length > 0 && (
        <ul>
          {fichiers.map((item, index) => (
            <li key={index}>
              {item.fichier.name}
              {' — '}
              {item.statut === 'en_attente' && 'en attente'}
              {item.statut === 'envoi' && 'envoi en cours...'}
              {item.statut === 'succes' && '✅ envoyé'}
              {item.statut === 'erreur' && `❌ ${item.erreur}`}
              {item.statut === 'en_attente' && (
                <button onClick={() => retirerFichier(index)}>Retirer</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <button onClick={envoyerTousLesFichiers} disabled={envoiEnCours}>
        {envoiEnCours ? 'Envoi en cours...' : `Envoyer (${fichiers.length})`}
      </button>

      {erreur && <p style={{ color: 'red' }}>{erreur}</p>}
    </div>
  );
}

export default PageInviteMultiple;